import { pdf } from '@react-pdf/renderer';
import classNames from 'classnames';
import html2canvas from 'html2canvas';
import React, { useMemo, useState } from 'react';
import Select, { MultiValue } from 'react-select';
import { DateValueType } from '../../utils/types/date_range';
import { OrganizationUnit } from '../../pages/admin/OrganizationSettings/TreeStructure';
import {
  AnalyticsSearchObject,
  ChartOrderItem,
  FormattedSelectOptions,
} from '../../utils/types/analytics/analytics_types';
import { SelectOptions } from '../../utils/types/react_select_types';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { ChevronDown, ChevronUp, LoadingIcon } from '../icons/icons';
import { DatePicker } from '../input/date_picker/date_picker';
import { SecondaryTypography } from '../typography/typography';
import styles from './data_source_filter.module.css';
import { MultiSelectListItem } from './MultiSelectListItem';
import { PdfCreator } from './PdfCreator';


type CollapsibleFilterProps = {
  open: boolean;
  skillList: SelectOptions[] | undefined;
  assessmentList?: SelectOptions[] | undefined;
  participantNo?: SelectOptions[] | undefined;
  organizationTree: OrganizationUnit | undefined;
  assessmentTypeList: SelectOptions[] | undefined;
  searchObject: AnalyticsSearchObject;
  setSearchObject: (searchObject: AnalyticsSearchObject) => void;
  searchResultChartOrder: ChartOrderItem[] | undefined;
  type?: string;
};

export const getFormattedOrgList = (
  level: number,
  organisationUnit?: OrganizationUnit,
): FormattedSelectOptions[] => {
  const formattedOrgList: FormattedSelectOptions[] = [];

  if (!organisationUnit) return formattedOrgList;
  formattedOrgList.push({
    value: organisationUnit.id,
    label: organisationUnit.unitName,
    level,
  });

  for (const org of organisationUnit.children) {
    formattedOrgList.push(...getFormattedOrgList(level + 1, org));
  }

  return formattedOrgList;
};

export const CollapsibleFilter = ({
  open,
  skillList,
  assessmentList,
  participantNo,
  organizationTree,
  assessmentTypeList,
  searchObject,
  setSearchObject,
  searchResultChartOrder,
  type,
}: CollapsibleFilterProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(open);
  const [pdfLoading, setPdfLoading] = useState(false);

  const formattedOrgList = useMemo(
    () => getFormattedOrgList(0, organizationTree),
    [organizationTree],
  );

  const toggleCollapsible = (open: boolean) => {
    setIsOpen(!open);
  };

  const handleTimeChange = (timeRange: { startDate: string | null; endDate: string | null }) => {
    setSearchObject({
      ...searchObject,
      timeRange,
    });
  };

  const handleMultiSelectChange = (
    selectedValues: MultiValue<SelectOptions>,
    action: any,
  ) => {
    if (action.action === 'remove-value') {
      setSearchObject({
        ...searchObject,
        [action.name]: '',
      });
    }
    setSearchObject({
      ...searchObject,
      [action.name]: selectedValues
        ? type === 'assessment'
          ? [selectedValues[selectedValues.length - 1]]
          : selectedValues
        : '',
    });
  };

  const handleSelectChange = (action: any, actionValues: any) => {
    setSearchObject({
      ...searchObject,
      [actionValues.name]: action,
    });
  };

  const handleOrgUnitSelect = (
    selectedValues: MultiValue<SelectOptions>,
    action: any,
  ) => {
    if (action.action === 'remove-value') {
      setSearchObject({
        ...searchObject,
        [action.name]: searchObject.organization.filter(
          (e) => e.value !== action.removedValue.value,
        ),
      });
      return;
    }

    if (action.action === 'deselect-option') {
      // Deselect all the child items along with the deselected one
      const deselectedVal: FormattedSelectOptions = action.option;
      const toRemove = [deselectedVal.value];
      const currentLevel = deselectedVal.level;
      const index = formattedOrgList.findIndex(
        (e) => e.value === deselectedVal.value,
      );
      for (let i = index + 1; i < formattedOrgList.length; i++) {
        const element = formattedOrgList[i];
        if (element.level > currentLevel) {
          toRemove.push(element.value);
        } else {
          break;
        }
      }
      setSearchObject({
        ...searchObject,
        [action.name]: searchObject.organization.filter(
          (e) => !toRemove.includes(e.value),
        ),
      });
      return;
    }

    if (action.action === 'select-option') {
      const selectCandidates = new Set(selectedValues);
      const selectedValue = action.option;
      const currentLevel = (selectedValue as FormattedSelectOptions).level;
      selectCandidates.add(selectedValue);

      const index = formattedOrgList.findIndex(
        (e) => e.value === selectedValue.value,
      );
      for (let i = index + 1; i < formattedOrgList.length; i++) {
        const element = formattedOrgList[i];
        if (element.level > currentLevel) {
          selectCandidates.add(element);
        } else {
          break;
        }
      }

      setSearchObject({
        ...searchObject,
        [action.name]: selectedValues
          ? type === 'assessment'
            ? [selectedValues[selectedValues.length - 1]]
            : Array.from(selectCandidates)
          : '',
      });
    }
  };

  const getFlatternFilterString = (searchArray: Array<SelectOptions>) => {
    return searchArray.map((item) => item.label).join(', ');
  };

  const getImageList = async () => {
    const imageList: Array<string> = [];
    if (!searchResultChartOrder) return;
    for (const item of searchResultChartOrder) {
      const id = item.chartId;
      const element = document.getElementById(id);
      if (element) {
        const canvas = await html2canvas(element);
        const data = canvas.toDataURL('image/jpg');
        imageList.push(data);
      }
    }
    return imageList;
  };

  const handleDashboardExport = async () => {
    setPdfLoading(true);
    const filterList: Array<string> = [];
    const assessmentType =
      'Assessment Type : ' +
      (searchObject.assessmentType.length
        ? getFlatternFilterString(searchObject.assessmentType)
        : 'N/A');
    const organization =
      'Organisation : ' +
      (searchObject.organization.length
        ? getFlatternFilterString(searchObject.organization)
        : 'N/A');
    const skill =
      'Skills : ' +
      (searchObject.skill.length
        ? getFlatternFilterString(searchObject.skill)
        : 'N/A');
    const timeRange =
      'Time Range: ' +
      (searchObject.timeRange?.startDate
        ? searchObject.timeRange?.startDate?.toString() +
          ' - ' +
          searchObject.timeRange?.endDate?.toString()
        : 'All');
    filterList.push(assessmentType, organization, skill, timeRange);

    const imageList = await getImageList();
    const doc = <PdfCreator imageList={imageList} filterList={filterList} />;
    const blob = await pdf(doc).toBlob();

    if (blob) {
      const fileURL = window.URL.createObjectURL(blob);
      let alink = document.createElement('a');
      alink.href = fileURL;
      alink.download = 'Analytics.pdf';
      alink.click();
    }
    setPdfLoading(false);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '16px',
        mb: 2.5,
        overflow: 'hidden',
        borderColor: isOpen ? 'primary.main' : 'divider',
        boxShadow: isOpen ? '0 4px 20px rgba(16, 185, 129, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.02)',
        transition: 'all 0.2s ease',
      }}
    >
      <Box
        onClick={() => toggleCollapsible(isOpen)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
          bgcolor: isOpen ? 'rgba(16, 185, 129, 0.03)' : 'background.paper',
          cursor: 'pointer',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.primary' }}>
          Data Source Filters
        </Typography>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapsible(isOpen);
          }}
          sx={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'none',
            color: 'text.secondary',
          }}
        >
          <ChevronDown />
        </IconButton>
      </Box>

      {isOpen && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            alignItems: { md: 'flex-end' },
            gap: 2,
            p: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ minWidth: 220, flex: '1 1 auto' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Time Range
            </Typography>
            <DatePicker
              startDate={searchObject.timeRange?.startDate ?? null}
              endDate={searchObject.timeRange?.endDate ?? null}
              onChange={handleTimeChange}
            />
          </Box>

          <Box sx={{ minWidth: 220, flex: '1 1 auto' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Organization Unit
            </Typography>
            <Select
              className={styles.selectContainer}
              placeholder="Select Organization Unit"
              name="organization"
              options={formattedOrgList}
              onChange={handleOrgUnitSelect}
              isClearable={true}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={
                type !== 'assessment' ? { Option: MultiSelectListItem } : {}
              }
              value={searchObject.organization}
              isMulti
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            />
          </Box>

          <Box sx={{ minWidth: 220, flex: '1 1 auto' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Assessment Type
            </Typography>
            <Select
              className={styles.selectContainer}
              placeholder="Select Assessment Type"
              name="assessmentType"
              options={assessmentTypeList}
              onChange={handleMultiSelectChange}
              isClearable={true}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={
                type !== 'assessment' ? { Option: MultiSelectListItem } : {}
              }
              value={searchObject.assessmentType}
              isMulti
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            />
          </Box>

          {type === 'assessment' ? (
            <Box sx={{ minWidth: 220, flex: '1 1 auto' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Assessment Name
              </Typography>
              <Select
                className={styles.selectContainer}
                placeholder="Select Assessment"
                name="assessmentName"
                options={assessmentList}
                onChange={handleSelectChange}
                isClearable={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={searchObject.assessmentName}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </Box>
          ) : (
            <Box sx={{ minWidth: 220, flex: '1 1 auto' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Skill
              </Typography>
              <Select
                className={styles.selectContainer}
                placeholder="Select Skill"
                name="skill"
                options={skillList}
                onChange={handleMultiSelectChange}
                isClearable={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option: MultiSelectListItem }}
                value={searchObject.skill}
                isMulti
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </Box>
          )}

          {type === 'assessment' && (
            <Box sx={{ minWidth: 200, flex: '1 1 auto' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                No of Top Participants
              </Typography>
              <Select
                className={styles.selectContainer}
                placeholder="Specify Participant count"
                name="participantNo"
                onChange={handleSelectChange}
                options={participantNo}
                isClearable={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={searchObject.participantNo}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleDashboardExport}
            disabled={pdfLoading}
            sx={{
              height: 38,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              px: 3,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {pdfLoading ? 'Generating...' : 'Export'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

